/**
 * VCI-P1 Slice 2 — Venue Partner Application Detail / Review Workspace
 * Route: /venue-commerce/partner-applications/:aid
 *
 * Full application lifecycle management:
 * - Review start → Info request → Inspection → Approve/Reject
 * - Duplicate check display
 * - Audit trail (review history)
 * Internal UAT only.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  AlertTriangle,
  Shield,
  Building2,
  MapPin,
  RefreshCw,
  Loader2,
  Plus,
  Search,
  ExternalLink,
  Users,
  Link,
  Info,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  APPLIED:       { label: "Applied",         icon: Clock,         cls: "bg-blue-50 text-blue-700 border-blue-200" },
  UNDER_REVIEW:  { label: "Under Review",    icon: Eye,           cls: "bg-amber-50 text-amber-700 border-amber-200" },
  INFO_REQUESTED:{ label: "Info Requested",  icon: AlertTriangle, cls: "bg-orange-50 text-orange-700 border-orange-200" },
  APPROVED:      { label: "Approved",        icon: CheckCircle,   cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  REJECTED:      { label: "Rejected",        icon: XCircle,       cls: "bg-red-50 text-red-700 border-red-200" },
};

function StageBadge({ stage }) {
  const cfg = STAGE_CONFIG[stage] || { label: stage, icon: Clock, cls: "bg-gray-50 text-gray-500 border-gray-200" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-sm px-3 py-1 rounded border font-medium ${cfg.cls}`}>
      <Icon size={13} /> {cfg.label}
    </span>
  );
}

function SectionCard({ title, children, className = "" }) {
  return (
    <div className={`bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-4 ${className}`}>
      {title && <h3 className="text-sm font-semibold text-gray-700 border-b border-gray-50 pb-2">{title}</h3>}
      {children}
    </div>
  );
}

export default function VenuePartnerApplicationDetail() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("info");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/venue-commerce/partner-applications/${aid}`, { withCredentials: true });
      setApp(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load application");
    } finally {
      setLoading(false);
    }
  }, [aid]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (m, type = "success") => { setMsg(m); setMsgType(type); setTimeout(() => setMsg(""), 5000); };

  const doAction = async (endpoint, payload, successMsg) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/venue-commerce/partner-applications/${aid}/${endpoint}`, payload, { withCredentials: true });
      setStatus(successMsg);
      load();
    } catch (err) {
      setStatus(err.response?.data?.detail || "Action failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const doPost = async (endpoint, payload, successMsg) => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/venue-commerce/partner-applications/${aid}/${endpoint}`, payload, { withCredentials: true });
      setStatus(successMsg);
      load();
    } catch (err) {
      setStatus(err.response?.data?.detail || "Action failed", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400 gap-2"><Loader2 size={20} className="animate-spin" /></div>;
  if (error) return <div className="p-6"><div className="bg-red-50 border border-red-200 rounded-xl p-5 text-red-700 text-sm">{error}</div></div>;
  if (!app) return null;

  const isTerminal = ["APPROVED", "REJECTED"].includes(app.stage);
  const canReview = ["APPLIED", "UNDER_REVIEW", "INFO_REQUESTED"].includes(app.stage);
  const canDecide = ["UNDER_REVIEW", "INFO_REQUESTED"].includes(app.stage);

  return (
    <div className="p-6 space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/venue-commerce/partner-applications")}
          className="gap-1 text-gray-500 -ml-1" data-testid="vci-back-to-apps">
          <ArrowLeft size={14} /> Back
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-lg font-semibold text-gray-900" data-testid="vci-app-title">
              {app.application_number}
            </h1>
            <StageBadge stage={app.stage} />
            {(app.duplicate_check?.matches || []).some(m => m.confidence === "HIGH") && (
              <span className="inline-flex items-center gap-1 text-xs text-orange-700 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded font-medium">
                <AlertTriangle size={10} /> Duplicate match found
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
              <Shield size={10} /> Internal UAT
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {app.venue_name} · {app.venue_city}
            {app.venue_type && ` · ${app.venue_type.replace(/_/g, " ")}`}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="gap-1 text-gray-500" data-testid="vci-app-refresh">
          <RefreshCw size={14} />
        </Button>
      </div>

      {/* Status message */}
      {msg && (
        <div className={`text-sm px-4 py-2 rounded-lg ${msgType === "error" ? "bg-red-50 text-red-700 border border-red-200" : "bg-green-50 text-green-700 border border-green-200"}`}
          data-testid="vci-app-msg">
          {msg}
        </div>
      )}

      {/* Approved linkage */}
      {app.stage === "APPROVED" && app.linked_supplier && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle size={18} className="text-emerald-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-emerald-800">
              Application Approved — {app.auto_created_supplier ? "New Venue Created" : "Linked to Existing Venue"}
            </p>
            <p className="text-xs text-emerald-700 mt-0.5">
              {app.linked_supplier.company_name || app.linked_supplier.display_name} · {app.linked_supplier.status}
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/venue-commerce/${app.linked_supplier_id}`)}
            className="gap-1 border-emerald-300 text-emerald-700"
            data-testid="vci-view-linked-venue"
          >
            <ExternalLink size={13} /> View Venue
          </Button>
        </div>
      )}

      <div className="grid grid-cols-3 gap-5">
        {/* Left column: Application data */}
        <div className="col-span-2 space-y-5">
          {/* Applicant info */}
          <SectionCard title="Applicant">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <InfoRow label="Name" value={app.applicant_name} testId="vci-app-applicant-name" />
              <InfoRow label="Email" value={app.applicant_email} testId="vci-app-applicant-email" />
              <InfoRow label="Phone" value={app.applicant_phone} testId="vci-app-applicant-phone" />
              {app.applicant_designation && <InfoRow label="Designation" value={app.applicant_designation} />}
            </div>
          </SectionCard>

          {/* Venue info */}
          <SectionCard title="Venue / Company Details">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <InfoRow label="Venue Name" value={app.venue_name} testId="vci-app-venue-name" />
              <InfoRow label="City" value={app.venue_city} />
              <InfoRow label="Type" value={app.venue_type?.replace(/_/g, " ") || "—"} />
              {app.company_name && <InfoRow label="Company" value={app.company_name} />}
              {app.capacity_estimate && <InfoRow label="Capacity" value={`${app.capacity_estimate} guests`} />}
              {app.events_per_year && <InfoRow label="Events/Year" value={app.events_per_year} />}
              {app.website && <InfoRow label="Website" value={app.website} />}
              {app.gst_ref && <InfoRow label="GST" value={app.gst_ref} />}
            </div>
            {app.additional_notes && (
              <div className="mt-2 pt-2 border-t border-gray-50">
                <p className="text-xs text-gray-400 mb-1">Additional Notes</p>
                <p className="text-sm text-gray-700">{app.additional_notes}</p>
              </div>
            )}
          </SectionCard>

          {/* Duplicate check */}
          <DuplicateCheckSection aid={aid} app={app} onRefresh={load} setMsg={setStatus} />

          {/* Inspection notes */}
          {(app.inspection_notes || []).length > 0 && (
            <SectionCard title={`Inspection Notes (${app.inspection_notes.length})`}>
              {app.inspection_notes.map(note => (
                <div key={note.note_id} className="bg-gray-50 rounded-lg p-3" data-testid={`vci-inspection-${note.note_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-gray-500">
                      {note.inspector_email} · {note.visit_date || "No date"} · {new Date(note.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-sm text-gray-800">{note.summary}</p>
                  {note.evidence_urls?.length > 0 && (
                    <p className="text-xs text-indigo-600 mt-1">{note.evidence_urls.length} evidence item(s)</p>
                  )}
                </div>
              ))}
            </SectionCard>
          )}

          {/* Review history */}
          <SectionCard title="Activity Timeline">
            <div className="space-y-3">
              {[...(app.review_history || [])].reverse().map((ev, i) => (
                <div key={i} className="flex gap-3" data-testid={`vci-history-${i}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs font-medium text-gray-700">{ev.action.replace(/_/g, " ")}</p>
                      {ev.from_stage && ev.to_stage && (
                        <span className="text-xs text-gray-400">{ev.from_stage} → {ev.to_stage}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{ev.actor_email} · {new Date(ev.timestamp).toLocaleString()}</p>
                    {ev.notes && <p className="text-xs text-gray-600 mt-0.5 italic">{ev.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {/* Right column: Actions */}
        <div className="space-y-4">
          {/* Consent */}
          <SectionCard title="Consent">
            {app.consent?.accepted ? (
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-emerald-700 font-medium">Accepted</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {app.consent.source} · {new Date(app.consent.timestamp).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-red-600">No consent record</p>
            )}
          </SectionCard>

          {/* Actions */}
          {!isTerminal && (
            <ActionsPanel app={app} canDecide={canDecide} canReview={canReview} saving={saving}
              doAction={doAction} doPost={doPost} />
          )}

          {/* Terminal state */}
          {isTerminal && (
            <SectionCard>
              {app.stage === "APPROVED" ? (
                <div className="text-center py-2">
                  <CheckCircle size={28} className="text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-emerald-700">Application Approved</p>
                  <p className="text-xs text-gray-500 mt-1">{app.decided_at ? new Date(app.decided_at).toLocaleDateString() : ""}</p>
                </div>
              ) : (
                <div className="text-center py-2">
                  <XCircle size={28} className="text-red-500 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-red-700">Application Rejected</p>
                  {app.rejection_reason && <p className="text-xs text-gray-500 mt-1 italic">{app.rejection_reason}</p>}
                </div>
              )}
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, testId }) {
  return (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-800" data-testid={testId}>{value || "—"}</p>
    </div>
  );
}

function DuplicateCheckSection({ aid, app, onRefresh, setMsg }) {
  const [running, setRunning] = useState(false);
  const dup = app.duplicate_check || {};
  const matches = dup.matches || [];
  const highConf = matches.filter(m => m.confidence === "HIGH");

  const runCheck = async () => {
    setRunning(true);
    try {
      await axios.get(`${API}/api/venue-commerce/partner-applications/${aid}/duplicate-check`, { withCredentials: true });
      setMsg("Duplicate check refreshed.");
      onRefresh();
    } catch (err) {
      setMsg(err.response?.data?.detail || "Duplicate check failed", "error");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className={`rounded-xl p-4 border shadow-sm ${highConf.length > 0 ? "bg-orange-50 border-orange-200" : matches.length > 0 ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}
      data-testid="vci-dup-check-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-sm font-semibold ${highConf.length > 0 ? "text-orange-800" : matches.length > 0 ? "text-amber-800" : "text-green-800"}`}>
          Duplicate Detection
        </h3>
        <Button variant="ghost" size="sm" onClick={runCheck} disabled={running} className="h-6 px-2 text-xs gap-1"
          data-testid="vci-run-dup-check">
          {running ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />}
          Refresh
        </Button>
      </div>
      {matches.length === 0 ? (
        <p className={`text-xs ${highConf.length > 0 ? "text-orange-700" : "text-green-700"}`}>
          No matching suppliers found — safe to create new.
        </p>
      ) : (
        <div className="space-y-2">
          {matches.map((m, i) => (
            <div key={i} className="bg-white rounded-lg p-2.5 border border-orange-200">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-800">{m.company_name}</p>
                <Badge variant="outline" className={`text-xs px-1.5 py-0 ${m.confidence === "HIGH" ? "border-red-200 text-red-700" : "border-amber-200 text-amber-700"}`}>
                  {m.confidence}
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Match: {m.match_field} · {m.email} · {m.phone}
              </p>
              <p className="text-xs text-gray-400 font-mono">{m.supplier_id}</p>
            </div>
          ))}
        </div>
      )}
      {dup.checked_at && (
        <p className="text-xs text-gray-400 mt-2">Checked: {new Date(dup.checked_at).toLocaleString()}</p>
      )}
    </div>
  );
}

function ActionsPanel({ app, canDecide, canReview, saving, doAction, doPost }) {
  const [reviewNotes, setReviewNotes] = useState("");
  const [infoText, setInfoText] = useState("");
  const [inspection, setInspection] = useState({ visit_date: "", summary: "" });
  const [approveNotes, setApproveNotes] = useState("");
  const [linkSupplierId, setLinkSupplierId] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [openPanel, setOpenPanel] = useState(null);

  return (
    <div className="space-y-3">
      {/* Start Review */}
      {app.stage === "APPLIED" && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
          <p className="text-sm font-semibold text-gray-700">Start Review</p>
          <Textarea
            value={reviewNotes}
            onChange={e => setReviewNotes(e.target.value)}
            placeholder="Optional review notes..."
            rows={2}
            className="text-xs resize-none"
            data-testid="vci-review-notes"
          />
          <Button
            size="sm"
            className="w-full gap-2"
            disabled={saving}
            onClick={() => doAction("start-review", { notes: reviewNotes || null }, "Review started.")}
            data-testid="vci-start-review-btn"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
            Start Review
          </Button>
        </div>
      )}

      {/* Request Information */}
      {app.stage === "UNDER_REVIEW" && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
          <p className="text-sm font-semibold text-gray-700">Request More Information</p>
          <Textarea
            value={infoText}
            onChange={e => setInfoText(e.target.value)}
            placeholder="Describe what information is needed..."
            rows={3}
            className="text-xs resize-none"
            data-testid="vci-info-request-text"
          />
          <Button
            size="sm"
            variant="outline"
            className="w-full gap-2"
            disabled={saving || !infoText.trim()}
            onClick={() => doAction("request-information", { info_request_text: infoText }, "Information requested.")}
            data-testid="vci-request-info-btn"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <AlertTriangle size={13} />}
            Request Information
          </Button>
        </div>
      )}

      {/* Add Inspection */}
      {["UNDER_REVIEW", "INFO_REQUESTED"].includes(app.stage) && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3">
          <p className="text-sm font-semibold text-gray-700">Add Inspection Note</p>
          <Input
            type="date"
            value={inspection.visit_date}
            onChange={e => setInspection(f => ({ ...f, visit_date: e.target.value }))}
            className="h-8 text-xs"
            data-testid="vci-inspection-visit-date"
          />
          <Textarea
            value={inspection.summary}
            onChange={e => setInspection(f => ({ ...f, summary: e.target.value }))}
            placeholder="Inspection summary..."
            rows={2}
            className="text-xs resize-none"
            data-testid="vci-inspection-summary"
          />
          <Button
            size="sm"
            variant="outline"
            className="w-full gap-2"
            disabled={saving || !inspection.summary.trim()}
            onClick={() => doPost("inspection",
              { visit_date: inspection.visit_date || null, summary: inspection.summary, evidence_urls: [] },
              "Inspection note added.")}
            data-testid="vci-add-inspection-btn"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Add Inspection
          </Button>
        </div>
      )}

      {/* Approve */}
      {canDecide && (
        <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm space-y-3">
          <p className="text-sm font-semibold text-gray-700">Approve Application</p>
          <Input
            value={linkSupplierId}
            onChange={e => setLinkSupplierId(e.target.value)}
            placeholder="Link existing supplier ID (optional)"
            className="h-8 text-xs font-mono"
            data-testid="vci-approve-link-supplier"
          />
          <p className="text-xs text-gray-400">Leave blank to create a new supplier from application data.</p>
          <Textarea
            value={approveNotes}
            onChange={e => setApproveNotes(e.target.value)}
            placeholder="Approval notes (optional)..."
            rows={2}
            className="text-xs resize-none"
            data-testid="vci-approve-notes"
          />
          <Button
            size="sm"
            className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
            disabled={saving}
            onClick={() => doAction("approve", {
              approval_notes: approveNotes || null,
              link_existing_supplier_id: linkSupplierId.trim() || null,
            }, "Application approved.")}
            data-testid="vci-approve-btn"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
            Approve
          </Button>
        </div>
      )}

      {/* Reject */}
      {canDecide && (
        <div className="bg-white border border-red-200 rounded-xl p-4 shadow-sm space-y-3">
          <p className="text-sm font-semibold text-gray-700">Reject Application</p>
          <Textarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Reason for rejection (required)..."
            rows={2}
            className="text-xs resize-none border-red-200"
            data-testid="vci-reject-reason"
          />
          <Button
            size="sm"
            variant="outline"
            className="w-full gap-2 border-red-200 text-red-700 hover:bg-red-50"
            disabled={saving || !rejectReason.trim()}
            onClick={() => doAction("reject", { rejection_reason: rejectReason }, "Application rejected.")}
            data-testid="vci-reject-btn"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <XCircle size={13} />}
            Reject
          </Button>
        </div>
      )}
    </div>
  );
}
